export type ZipEntry = { path: string; data: Uint8Array }

/** CRC-32 computation for ZIP file integrity checks. */
export const crc32 = (data: Uint8Array): number => {
  let crc = 0xffffffff
  for (let i = 0; i < data.length; i++) {
    crc ^= data[i]
    for (let bit = 0; bit < 8; bit++) {
      crc = crc & 1 ? (crc >>> 1) ^ 0xedb88320 : crc >>> 1
    }
  }
  return (crc ^ 0xffffffff) >>> 0
}

export const buildZipBlob = (entries: ZipEntry[]): Uint8Array => {
  const textEncoder = new TextEncoder()
  const localHeaders: Uint8Array[] = []
  const centralHeaders: Uint8Array[] = []
  let offset = 0

  for (const entry of entries) {
    const pathBytes = textEncoder.encode(entry.path)
    const crc = crc32(entry.data)

    // Local file header (30 bytes + path + data)
    const localHeader = new Uint8Array(30 + pathBytes.length + entry.data.length)
    const localView = new DataView(localHeader.buffer)
    localView.setUint32(0, 0x04034b50, true)
    localView.setUint16(4, 20, true)
    localView.setUint16(6, 0, true)
    localView.setUint16(8, 0, true)
    localView.setUint16(10, 0, true)
    localView.setUint16(12, 0, true)
    localView.setUint32(14, crc, true)
    localView.setUint32(18, entry.data.length, true)
    localView.setUint32(22, entry.data.length, true)
    localView.setUint16(26, pathBytes.length, true)
    localView.setUint16(28, 0, true)
    localHeader.set(pathBytes, 30)
    localHeader.set(entry.data, 30 + pathBytes.length)
    localHeaders.push(localHeader)

    // Central directory header (46 bytes + path)
    const centralHeader = new Uint8Array(46 + pathBytes.length)
    const centralView = new DataView(centralHeader.buffer)
    centralView.setUint32(0, 0x02014b50, true)
    centralView.setUint16(4, 20, true)
    centralView.setUint16(6, 20, true)
    centralView.setUint16(8, 0, true)
    centralView.setUint16(10, 0, true)
    centralView.setUint16(12, 0, true)
    centralView.setUint16(14, 0, true)
    centralView.setUint32(16, crc, true)
    centralView.setUint32(20, entry.data.length, true)
    centralView.setUint32(24, entry.data.length, true)
    centralView.setUint16(28, pathBytes.length, true)
    centralView.setUint16(30, 0, true)
    centralView.setUint16(32, 0, true)
    centralView.setUint16(34, 0, true)
    centralView.setUint16(36, 0, true)
    centralView.setUint32(38, 0, true)
    centralView.setUint32(42, offset, true)
    centralHeader.set(pathBytes, 46)
    centralHeaders.push(centralHeader)

    offset += localHeader.length
  }

  const centralDirSize = centralHeaders.reduce((sum, header) => sum + header.length, 0)
  const centralDirOffset = offset

  // End of central directory (22 bytes)
  const endRecord = new Uint8Array(22)
  const endView = new DataView(endRecord.buffer)
  endView.setUint32(0, 0x06054b50, true)
  endView.setUint16(4, 0, true)
  endView.setUint16(6, 0, true)
  endView.setUint16(8, entries.length, true)
  endView.setUint16(10, entries.length, true)
  endView.setUint32(12, centralDirSize, true)
  endView.setUint32(16, centralDirOffset, true)
  endView.setUint16(20, 0, true)

  const totalSize = offset + centralDirSize + 22
  const result = new Uint8Array(totalSize)
  let position = 0

  for (const header of localHeaders) {
    result.set(header, position)
    position += header.length
  }
  for (const header of centralHeaders) {
    result.set(header, position)
    position += header.length
  }
  result.set(endRecord, position)

  return result
}
